import React, { useCallback, useEffect, useState } from "react";
import { Text, View } from "react-native";
import moment from "moment";
import tw from "twrnc";

import analytics from "@/analytics";
import AppModal from "@/components/shared/Modal";
import { notify } from "@/components/shared/AppToast";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import Button, { ButtonRow } from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { TextField } from "@/components/settings/Field";
import IconButton from "@/components/settings/IconButton";
import { ChoiceGroup } from "@/components/settings/SettingRow";
import { SettingsSection } from "@/components/settings/SettingsPage";
import { colors, text } from "@/components/settings/theme";
import { nounInSentence, t, tc, tn } from "@/localization";
import {
  fetchTeamMembers,
  inviteTeamMember,
  removeTeamMember,
  resendTeamInvite,
  updateTeamMember,
} from "./api";
import { teamsErrorMessage } from "./errors";
import RolePill from "./RolePill";
import { ROLE_DESCRIPTIONS, ROLE_LABELS, ROLES } from "./roles";

// The people of an account (Profile › Team) or of one event (event › People):
// invite by email with a role, see who joined and who hasn't yet, send an
// invite again, change a role, remove someone (PLAN.md §5.4, CONTRACT.md
// "Teams Lambda"). The server checks every action; this lists what it allows.

const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@]+\.[^\s@]+$/;

const roleOptions = (scope) =>
  ROLES.map((role) => ({
    value: role,
    label: ROLE_LABELS[role],
    description: ROLE_DESCRIPTIONS[scope][role],
  }));

const dateText = (iso) => {
  const m = iso ? moment(iso) : null;
  return m && m.isValid() ? m.format(t("MMM D, YYYY")) : null;
};

// The signed-in user's own row (they don't manage themselves here).
const isSelfRow = (member, self) =>
  !!self && (member.id === `A:${self}` || member.id?.endsWith(`:${self}`));

const MemberRow = ({
  member,
  self,
  canManage,
  busy,
  onRole,
  onRemove,
  onResend,
}) => {
  const pending = member.status === "pending";
  const mine = isSelfRow(member, self);
  const title = member.name || member.email || t("Someone");
  const details = [
    member.name && member.email ? member.email : null,
    pending
      ? member.expires_at
        ? t("Invite expires {date}", { date: dateText(member.expires_at) })
        : t("Invited")
      : member.accepted_at
      ? t("Joined {date}", { date: dateText(member.accepted_at) })
      : null,
  ].filter(Boolean);
  return (
    <View
      style={[
        tw`flex-row items-center py-3`,
        { borderBottomWidth: 1, borderBottomColor: colors.divider },
      ]}
    >
      <View style={tw`flex-1 mr-3`}>
        <View style={tw`flex-row items-center flex-wrap`}>
          <Text style={[text.bodyStrong, tw`mr-2`]} numberOfLines={1}>
            {mine ? t("{name} (you)", { name: title }) : title}
          </Text>
          <RolePill role={member.role} />
          {pending && (
            <RolePill text={t("Invited")} tone="pending" style={tw`ml-1.5`} />
          )}
        </View>
        {!!details.length && (
          <Text style={[text.hint, tw`mt-0.5`]} numberOfLines={2}>
            {details.join(" · ")}
          </Text>
        )}
      </View>
      {canManage && !mine && (
        <View style={tw`flex-row items-center`}>
          {pending ? (
            <IconButton
              icon="mail-outline"
              label={t("Send the invite to {name} again", { name: title })}
              disabled={busy}
              onPress={() => onResend(member)}
            />
          ) : (
            <IconButton
              icon="create-outline"
              label={t("Change {name}'s role", { name: title })}
              disabled={busy}
              onPress={() => onRole(member)}
            />
          )}
          <IconButton
            icon="trash-outline"
            label={
              pending
                ? t("Withdraw the invite to {name}", { name: title })
                : tc("team member", "Remove {name}", { name: title })
            }
            disabled={busy}
            onPress={() => onRemove(member)}
          />
        </View>
      )}
    </View>
  );
};

const RoleModal = ({ member, scope, onClose, onSave }) => {
  const [role, setRole] = useState(member?.role || "editor");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (member) setRole(member.role);
  }, [member?.id]);
  const save = async () => {
    if (!member || saving) return;
    setSaving(true);
    try {
      await onSave(member, role);
    } finally {
      setSaving(false);
    }
  };
  const title = member?.name || member?.email || "";
  return (
    <AppModal
      visible={!!member}
      onRequestClose={() => !saving && onClose()}
      maxHeight={520}
    >
      <Text style={[text.modalTitle, tw`mb-1 mr-6`]}>{t("Change role")}</Text>
      <Text style={[text.hint, tw`mb-3`]} numberOfLines={1}>
        {title}
      </Text>
      <ChoiceGroup
        accessibilityLabel={t("Role")}
        value={role}
        onChange={setRole}
        options={roleOptions(scope)}
      />
      <Button
        text={t("Save role")}
        variant="primary"
        loading={saving}
        disabled={role === member?.role}
        onPress={save}
        style={tw`mt-4`}
      />
      <Button
        text={t("Cancel")}
        variant="tertiary"
        fullWidth={false}
        style={tw`self-center mt-2`}
        disabled={saving}
        onPress={onClose}
      />
    </AppModal>
  );
};

// Removing someone who joined: they may have copied stream keys, and a key is
// all an encoder needs to stream, so new keys for the upcoming events they
// could open are the default (eventlive-cdk/teams/src/streamKeys.ts). Events
// that streamed or have video keep their keys: the key is the video's id.
const RemoveModal = ({ member, scope, onClose, onRemove }) => {
  const [resetKeys, setResetKeys] = useState(true);
  const [removing, setRemoving] = useState(false);
  useEffect(() => {
    if (member) setResetKeys(true);
  }, [member?.id]);
  const remove = async () => {
    if (!member || removing) return;
    setRemoving(true);
    try {
      await onRemove(member, resetKeys);
    } finally {
      setRemoving(false);
    }
  };
  const who = member?.name || member?.email;
  return (
    <AppModal
      visible={!!member}
      onRequestClose={() => !removing && onClose()}
      maxHeight={560}
    >
      <Text style={[text.modalTitle, tw`mb-1 mr-6`]}>
        {who ? t("Remove {name}?", { name: who }) : t("Remove them?")}
      </Text>
      <Text style={[text.hint, tw`mb-3`]}>
        {scope === "event"
          ? t("They lose access to this event right away.")
          : t("They lose access to the account right away.")}
      </Text>
      <ChoiceGroup
        accessibilityLabel={t("Stream keys")}
        value={resetKeys}
        onChange={setResetKeys}
        options={[
          {
            value: true,
            label: t("Change the stream keys too"),
            description:
              scope === "event"
                ? t(
                    "Recommended. This event and its rooms get new keys if they haven't streamed yet. Update any encoder, OBS or camera link with the new key from How to Go Live."
                  )
                : t(
                    "Recommended. Upcoming events that haven't streamed yet get new keys. Update any encoder, OBS or camera link with the new key from How to Go Live."
                  ),
          },
          {
            value: false,
            label: t("Keep the stream keys"),
            description: t(
              "Encoders set up with the current keys keep working, and so would anyone who copied one."
            ),
          },
        ]}
      />
      <Button
        text={t("Remove")}
        variant="danger"
        loading={removing}
        onPress={remove}
        style={tw`mt-4`}
      />
      <Button
        text={t("Cancel")}
        variant="tertiary"
        fullWidth={false}
        style={tw`self-center mt-2`}
        disabled={removing}
        onPress={onClose}
      />
    </AppModal>
  );
};

const TeamMembers = ({
  slug = null,
  self,
  canManage = true,
  accountTeamNote,
  onManageAccountTeam,
}) => {
  const scope = slug ? "event" : "account";
  const { showActionSheetWithOptions } = useActionSheet();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMembers(await fetchTeamMembers(slug));
      setLoadError(null);
    } catch (err) {
      setLoadError(
        teamsErrorMessage(err, t("Couldn't load the people. Try again."))
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const cleanEmail = email.trim().toLowerCase();
  const emailError =
    tried && !EMAIL_RE.test(cleanEmail)
      ? t("Enter a valid email address.")
      : null;

  const sendInvite = async () => {
    setTried(true);
    if (!EMAIL_RE.test(cleanEmail) || sending) return;
    setSending(true);
    setInviteError(null);
    try {
      await inviteTeamMember({ email: cleanEmail, role, slug });
      analytics.track("Team Invite Sent", { role, scope, resend: false });
      notify.success(
        t("Invite sent"),
        t("We emailed {email} a link to join.", { email: cleanEmail })
      );
      setEmail("");
      setTried(false);
      load();
    } catch (err) {
      const message = teamsErrorMessage(
        err,
        t("Couldn't send the invite. Try again.")
      );
      setInviteError(message);
      notify.error(t("Invite not sent"), message);
    } finally {
      setSending(false);
    }
  };

  const act = async (member, work, done) => {
    setBusyId(member.id);
    try {
      await work();
      if (done) notify.success(done);
      await load();
    } catch (err) {
      notify.error(t("That didn't work"), teamsErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const resend = (member) =>
    act(
      member,
      async () => {
        await resendTeamInvite(member.id);
        analytics.track("Team Invite Sent", {
          role: member.role,
          scope: member.slug ? "event" : "account",
          resend: true,
        });
      },
      t("Invite sent again")
    );

  const remove = (member) => {
    if (member.status !== "pending") {
      setRemoving(member);
      return;
    }
    const who = member.name || member.email;
    showActionSheetWithOptions(
      {
        options: [t("Withdraw invite"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: who
          ? t("Withdraw the invite to {name}?", { name: who })
          : t("Withdraw the invite to them?"),
        message: t("The link in their email stops working."),
      },
      (index) => {
        if (index !== 0) return;
        act(member, () => removeTeamMember(member.id), t("Invite withdrawn"));
      }
    );
  };

  const confirmRemove = async (member, resetStreamKeys) => {
    await act(
      member,
      async () => {
        await removeTeamMember(member.id, { resetStreamKeys });
        analytics.track("Team Member Removed", {
          role: member.role,
          scope: member.slug ? "event" : "account",
          resetStreamKeys,
        });
      },
      resetStreamKeys
        ? t("Removed. Upcoming events have new stream keys.")
        : tc("team member", "Removed")
    );
    setRemoving(null);
  };

  const saveRole = async (member, nextRole) => {
    try {
      await updateTeamMember(member.id, nextRole);
      notify.success(
        t("Role changed"),
        t("{name} is now {role}.", {
          name: member.name || member.email,
          role: nounInSentence(ROLE_LABELS[nextRole]),
        })
      );
      setEditing(null);
      await load();
    } catch (err) {
      notify.error(t("Role not changed"), teamsErrorMessage(err));
    }
  };

  const active = members.filter((m) => m.status === "active");
  const pending = members.filter((m) => m.status === "pending");
  // On an event's People: its own people first; the account's team can work
  // on every event, so it's listed after them, to see who else has access.
  const own = slug ? active.filter((m) => m.slug) : active;
  const ownInvites = slug ? pending.filter((m) => m.slug) : pending;
  const accountTeam = slug ? active.filter((m) => !m.slug) : [];

  const rows = (list) =>
    list.map((member) => (
      <MemberRow
        key={member.id}
        member={member}
        self={self}
        canManage={canManage}
        busy={busyId === member.id}
        onRole={setEditing}
        onRemove={remove}
        onResend={resend}
      />
    ));

  return (
    <>
      {canManage && (
        <SettingsSection
          first
          title={slug ? t("Invite someone to this event") : t("Invite someone")}
          description={t(
            "They get an email with a link that works for 14 days, and accept with their own sign-in."
          )}
        >
          <TextField
            label={t("Email address")}
            value={email}
            onChangeText={(value) => {
              setEmail(value);
              if (inviteError) setInviteError(null);
            }}
            placeholder={t("e.g. sam@example.com")}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="email"
            error={emailError}
            onSubmitEditing={sendInvite}
          />
          <Text style={[text.label, tw`mb-1.5`]}>{t("Role")}</Text>
          <ChoiceGroup
            accessibilityLabel={t("Role")}
            value={role}
            onChange={setRole}
            options={roleOptions(scope)}
          />
          <ButtonRow align="start" style={tw`mt-4`}>
            <Button
              text={t("Send invite")}
              variant="primary"
              size="md"
              fullWidth={false}
              icon="mail-outline"
              loading={sending}
              onPress={sendInvite}
            />
          </ButtonRow>
          {!!inviteError && (
            <Callout tone="warning" style={tw`mt-3`} live>
              {inviteError}
            </Callout>
          )}
        </SettingsSection>
      )}

      {!!loadError && (
        <Callout
          tone="danger"
          style={tw`mt-6`}
          action={
            <Button
              text={t("Try again")}
              size="sm"
              fullWidth={false}
              onPress={load}
            />
          }
        >
          {loadError}
        </Callout>
      )}

      {!loadError && (
        <SettingsSection
          first={!canManage}
          title={slug ? t("People on this event") : t("People")}
          description={
            loading && !members.length
              ? t("Loading…")
              : own.length
              ? tn("{count} person", "{count} people", own.length)
              : undefined
          }
        >
          {!loading && !own.length && (
            <Text style={text.hint}>
              {canManage
                ? t("No one yet. Invite someone above.")
                : t("No one yet.")}
            </Text>
          )}
          {rows(own)}
        </SettingsSection>
      )}

      {!loadError && !!ownInvites.length && (
        <SettingsSection
          title={t("Invites")}
          description={tn(
            "{count} waiting to be accepted",
            "{count} waiting to be accepted",
            ownInvites.length
          )}
        >
          {rows(ownInvites)}
        </SettingsSection>
      )}

      {!loadError && !!slug && !!accountTeam.length && (
        <SettingsSection
          title={t("Account team")}
          description={
            accountTeamNote ||
            t("They can work on every event in the account, this one included.")
          }
        >
          {accountTeam.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              self={self}
              canManage={false}
            />
          ))}
          {!!onManageAccountTeam && (
            <Button
              text={t("Manage the team")}
              variant="tertiary"
              fullWidth={false}
              style={tw`mt-2`}
              onPress={onManageAccountTeam}
            />
          )}
        </SettingsSection>
      )}

      <RoleModal
        member={editing}
        scope={editing?.slug ? "event" : "account"}
        onClose={() => setEditing(null)}
        onSave={saveRole}
      />
      <RemoveModal
        member={removing}
        scope={removing?.slug ? "event" : "account"}
        onClose={() => setRemoving(null)}
        onRemove={confirmRemove}
      />
    </>
  );
};

export default TeamMembers;
